import { pageState } from 'State/page'
import type { CallbackInterface } from 'recoil'

import type { PageStateType } from 'Types/state'

import type { SubscriptionStatus } from 'Types/beRecipientsApi'

import type {
  ColumnName,
  PageParams,
  PaginationState,
  SearchFiltersState,
} from 'Pages/RecipientsList/model'

import {
  getInitialState,
  updatePagePreferences,
} from 'Pages/RecipientsList/recoil/services/filters'

import {
  fetchDynamicFields,
  getExistingColumns,
} from 'Pages/RecipientsList/recoil/services/columns'

import { panelEditRecipientState } from 'Pages/RecipientsList/recoil/state/recipient'
import {
  emptyStateState,
  paginationState,
  resultsState,
  selectionState,
} from 'Pages/RecipientsList/recoil/state/results'

import { recipientsGroupsState } from 'Pages/RecipientsList/recoil/state/groups'

import {
  appliedSearchFiltersState,
  searchFiltersState,
} from 'Pages/RecipientsList/recoil/state/filters'

import { modalBulkFindReplaceState } from 'Pages/RecipientsList/recoil/state/modalBulkFindReplace'
import { modalPendingState } from 'Pages/RecipientsList/recoil/state/modalPending'
import { modalRecipientGroupsState } from 'Pages/RecipientsList/recoil/state/modalRecipientGroups'
import { modalRecipientTrackingState } from 'Pages/RecipientsList/recoil/state/modalRecipientTracking'
import { modalSendConfirmationState } from 'Pages/RecipientsList/recoil/state/modalSendConfirmation'
import { modalSubscribeState } from 'Pages/RecipientsList/recoil/state/modalSubscribe'
import {
  modalBulkAddToGroupState,
  modalBulkRemoveFromGroupsState,
  modalBulkUnsubscribeState,
  modalDelStatsContactState,
  modalDeleteState,
  modalUnsubscribeState,
} from 'Pages/RecipientsList/recoil/state/modals'

import {
  fetchEmptyState,
  fetchResults,
} from 'Pages/RecipientsList/recoil/services/results'

import { logAppError } from 'Helpers/logger'

import getIntl from 'Helpers/intl'
import { isNullOrEmpty } from 'Helpers/strings'
import {
  panelAddRecipientGroupsState,
  panelAddRecipientState,
} from 'Pages/RecipientsList/recoil/state/addRecipient'
import {
  columnsState,
  dynamicFieldsState,
} from 'Pages/RecipientsList/recoil/state/columns'
import {
  listsState,
  maskedFieldsState,
} from 'Pages/RecipientsList/recoil/state/common'
import { panelConfigColumnsState } from 'Pages/RecipientsList/recoil/state/configColumns'
import { modalAddSegmentState } from 'Pages/RecipientsList/recoil/state/modalAddSegment'

export const initPageCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (qsParams: PageParams) => {
    //
    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: true,
    }))

    const pagination = await snapshot.getPromise(paginationState)

    const forcedRecipient =
      !isNullOrEmpty(qsParams.rid) || !isNullOrEmpty(qsParams.nr)

    try {
      const [
        {
          columns,
          searchFilters,
          modalUnsubscribePreferences,
          modalBulkUnsubscribePreferences,
        },
        [dynamicFields, maskedFields],
      ] = await Promise.all([getInitialState(qsParams), fetchDynamicFields()])

      const isEmptyState = await fetchEmptyState()

      set(maskedFieldsState, maskedFields)

      if (isEmptyState) {
        set(emptyStateState, true)
      } else {
        const safeColumns = getExistingColumns(columns, dynamicFields)

        const { items, ...paginationOut } = await fetchResults(
          searchFilters,
          safeColumns,
          pagination,
          forcedRecipient,
        )

        set(dynamicFieldsState, dynamicFields)
        set(searchFiltersState, searchFilters)
        set(appliedSearchFiltersState, searchFilters)
        set(columnsState, safeColumns)
        set(resultsState, items)
        set(paginationState, (prev) => ({
          ...prev,
          ...paginationOut,
          forcedRecipient,
        }))
        set(modalUnsubscribeState, (prev) => ({
          ...prev,
          ...modalUnsubscribePreferences,
        }))
        set(modalBulkUnsubscribeState, (prev) => ({
          ...prev,
          ...modalBulkUnsubscribePreferences,
        }))
      }

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'ready',
      }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'initPageCallback',
        appError: error,
      })
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }

    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: false,
    }))
    //
  }

//  TODO
export const resetPageCallback =
  ({ reset }: CallbackInterface) =>
  async (): Promise<void> => {
    reset(pageState)

    reset(searchFiltersState)
    reset(appliedSearchFiltersState)
    reset(columnsState)
    reset(dynamicFieldsState)
    reset(resultsState)
    reset(paginationState)
    reset(emptyStateState)
    reset(selectionState)
    reset(maskedFieldsState)

    reset(modalUnsubscribeState)
    reset(modalDeleteState)
    reset(modalPendingState)
    reset(modalSendConfirmationState)
    reset(modalSubscribeState)
    reset(modalRecipientGroupsState)

    reset(modalBulkUnsubscribeState)
    reset(modalBulkAddToGroupState)
    reset(modalBulkRemoveFromGroupsState)
    reset(modalBulkFindReplaceState)
    reset(modalDelStatsContactState)
    reset(modalRecipientTrackingState)
    reset(modalAddSegmentState)

    reset(panelEditRecipientState)
    reset(panelAddRecipientState)
    reset(listsState)
    reset(recipientsGroupsState)
    reset(panelAddRecipientGroupsState)
    reset(panelConfigColumnsState)
  }

export const searchResultsCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    set(paginationState, (prev) => ({ ...prev, loading: true }))

    const [prevPagination, prevColumns, prevSearchFilters] = await Promise.all([
      snapshot.getPromise(paginationState),
      snapshot.getPromise(columnsState),
      snapshot.getPromise(searchFiltersState),
    ])

    const newPagination: PaginationState = {
      ...prevPagination,
      pageNumber: 1,
    }

    try {
      //
      const { items, ...paginationOut } = await fetchResults(
        prevSearchFilters,
        prevColumns,
        newPagination,
      )

      await updatePagePreferences({
        status: prevSearchFilters.status.value,
        columns: prevColumns,
      })

      set(resultsState, items)
      set(appliedSearchFiltersState, { ...prevSearchFilters })
      set(paginationState, (prev) => ({
        ...prev,
        ...newPagination,
        ...paginationOut,
        loading: false,
        forcedRecipient: false,
      }))

      reset(selectionState)

      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'searchResultsCallback',
        appError: error,
      })
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }
  }

export const fetchResultsCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (
    {
      pageNumber,
      pageSize,
      orderBy,
      orderDescending,
      columns,
      subscriptionStatus,
    }: {
      pageNumber?: number
      pageSize?: number
      orderBy?: ColumnName
      orderDescending?: boolean
      columns?: ColumnName[]
      subscriptionStatus?: SubscriptionStatus[]
    },
    resetSelection = false,
  ) => {
    set(paginationState, (prev) => ({ ...prev, loading: true }))

    const [prevPagination, prevColumns, prevSearchFilters] = await Promise.all([
      snapshot.getPromise(paginationState),
      snapshot.getPromise(columnsState),
      snapshot.getPromise(appliedSearchFiltersState),
    ])

    const newPagination: PaginationState = {
      ...prevPagination,
      pageNumber:
        pageNumber !== undefined ? pageNumber : prevPagination.pageNumber,
      pageSize: pageSize !== undefined ? pageSize : prevPagination.pageSize,
      orderBy: orderBy !== undefined ? orderBy : prevPagination.orderBy,
      orderDescending:
        orderDescending !== undefined
          ? orderDescending
          : orderBy !== undefined && orderBy === prevPagination.orderBy
            ? !prevPagination.orderDescending
            : prevPagination.orderDescending,
    }

    const newColumns: ColumnName[] =
      columns !== undefined ? columns : prevColumns

    const newSearchFilters =
      subscriptionStatus !== undefined
        ? {
            ...prevSearchFilters,
            status: {
              value: subscriptionStatus,
              dirty: false,
            },
          }
        : prevSearchFilters

    try {
      //
      const { items, ...paginationOut } = await fetchResults(
        newSearchFilters,
        newColumns,
        newPagination,
      )

      await updatePagePreferences({
        status: newSearchFilters.status.value,
      })

      set(resultsState, items)
      set(appliedSearchFiltersState, { ...newSearchFilters })
      set(paginationState, (prev) => ({
        ...prev,
        ...newPagination,
        ...paginationOut,
        loading: false,
        forcedRecipient: false,
      }))

      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'fetchResultsCallback',
        appError: error,
      })
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }
  }

export const resetResultsCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    set(paginationState, (prev) => ({ ...prev, loading: true }))

    const [prevPagination, prevColumns, prevSearchFilters] = await Promise.all([
      snapshot.getPromise(paginationState),
      snapshot.getPromise(columnsState),
      snapshot.getPromise(appliedSearchFiltersState),
    ])

    const newPagination: PaginationState = {
      ...prevPagination,
      pageNumber: 1,
    }

    //  TODO reset criteria to []
    const newSearchFilters: SearchFiltersState = {
      ...prevSearchFilters,
      contact: { value: '', dirty: false },
      criteria: { value: [], dirty: false },
    }

    try {
      //
      const { items, ...paginationOut } = await fetchResults(
        newSearchFilters,
        prevColumns,
        newPagination,
      )

      set(searchFiltersState, newSearchFilters)
      set(appliedSearchFiltersState, newSearchFilters)
      set(resultsState, items)
      set(paginationState, (prev) => ({
        ...prev,
        ...newPagination,
        ...paginationOut,
        loading: false,
        forcedRecipient: false,
      }))

      reset(selectionState)
      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'resetResultsCallback',
        appError: error,
      })
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }
  }
